
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Loader2, Heart, Mountain, Camera, Code, BookOpen, Users } from 'lucide-react';

interface InterestItem {
    id: string;
    name: string;
    description: string;
    icon: string;
    category: 'creative' | 'sport' | 'hobby' | 'learning';
}

export default function InterestsManager() {
    const [items, setItems] = useState<InterestItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const [newItem, setNewItem] = useState<Partial<InterestItem>>({
        name: '',
        description: '',
        icon: 'Heart',
        category: 'hobby'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'about_interests')
                .maybeSingle();

            if (error) throw error;
            if (data && data.content) {
                setItems(data.content);
            }
        } catch (err) {
            console.error('Error fetching interests:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (updatedList: InterestItem[]) => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'about_interests',
                    content: updatedList,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setItems(updatedList);
            setIsFormOpen(false);
            setNewItem({ name: '', description: '', icon: 'Heart', category: 'hobby' });
        } catch (err) {
            console.error('Error saving interests:', err);
            alert('Failed to save interests');
        } finally {
            setSaving(false);
        }
    };

    const addItem = () => {
        if (!newItem.name) return;

        const itemToAdd: InterestItem = {
            id: Math.random().toString(36).substr(2, 9),
            name: newItem.name!,
            description: newItem.description || '',
            icon: newItem.icon || 'Heart',
            category: newItem.category || 'hobby'
        };

        const updated = [...items, itemToAdd];
        handleSave(updated);
    };

    const deleteItem = (id: string) => {
        if (!confirm('Delete this interest?')) return;
        const updated = items.filter(i => i.id !== id);
        handleSave(updated);
    };

    const getIcon = (name: string) => {
        switch (name.toLowerCase()) {
            case 'heart': return <Heart size={24} />;
            case 'mountain': return <Mountain size={24} />;
            case 'camera': return <Camera size={24} />;
            case 'code': return <Code size={24} />;
            case 'bookopen': return <BookOpen size={24} />;
            case 'users': return <Users size={24} />;
            default: return <Heart size={24} />;
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'creative': return 'bg-purple-900/30 text-purple-400 border-purple-900';
            case 'sport': return 'bg-green-900/30 text-green-400 border-green-900';
            case 'hobby': return 'bg-blue-900/30 text-blue-400 border-blue-900';
            case 'learning': return 'bg-orange-900/30 text-orange-400 border-orange-900';
            default: return 'bg-gray-900/30 text-gray-400 border-gray-900';
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white">Personal Interests</h2>
                <button
                    onClick={() => setIsFormOpen(!isFormOpen)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                    {isFormOpen ? 'Cancel' : <><Plus size={18} /> Add Interest</>}
                </button>
            </div>

            {isFormOpen && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                            <input
                                type="text"
                                value={newItem.name}
                                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="e.g., Photography"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Category</label>
                            <select
                                value={newItem.category}
                                onChange={(e) => setNewItem({ ...newItem, category: e.target.value as any })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            >
                                <option value="hobby">Hobby</option>
                                <option value="sport">Sport</option>
                                <option value="creative">Creative</option>
                                <option value="learning">Learning</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">Icon</label>
                            <select
                                value={newItem.icon}
                                onChange={(e) => setNewItem({ ...newItem, icon: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            >
                                <option value="Heart">Heart</option>
                                <option value="Mountain">Mountain</option>
                                <option value="Camera">Camera</option>
                                <option value="Code">Code</option>
                                <option value="BookOpen">Book</option>
                                <option value="Users">Users</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                            <textarea
                                rows={2}
                                value={newItem.description}
                                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            onClick={addItem}
                            disabled={saving || !newItem.name}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="animate-spin h-5 w-5" /> : 'Save Interest'}
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((item) => (
                    <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex flex-col gap-3 group hover:border-gray-700 transition-colors relative">
                        <button
                            onClick={() => deleteItem(item.id)}
                            className="absolute top-2 right-2 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-2"
                        >
                            <Trash2 size={16} />
                        </button>

                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center text-blue-400">
                                {getIcon(item.icon)}
                            </div>
                            <div className="flex-grow">
                                <h3 className="font-semibold text-white">{item.name}</h3>
                                <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${getCategoryColor(item.category)}`}>
                                    {item.category}
                                </div>
                            </div>
                        </div>

                        {item.description && <p className="text-sm text-gray-400">{item.description}</p>}
                    </div>
                ))}

                {items.length === 0 && !loading && (
                    <div className="col-span-full text-center py-12 text-gray-500 border-2 border-dashed border-gray-800 rounded-xl">
                        No interests added yet.
                    </div>
                )}
            </div>
        </div>
    );
}
